import {apiUrl} from '../api/fetch'
import {DriverAccessError} from './errors'
import {parseDriverValidationFieldErrors} from './parse-driver-validation-errors'

export class DriverValidationError extends Error {
    fieldErrors: Record<string, string>

    constructor(fieldErrors: Record<string, string>) {
        super('Validation failed')
        this.name = 'DriverValidationError'
        this.fieldErrors = fieldErrors
    }
}

async function parseJsonResponse(res: Response): Promise<unknown> {
    try {
        return await res.json()
    } catch (error) {
        if (error instanceof SyntaxError) {
            if (res.status >= 502 && res.status <= 504) {
                throw new Error('API server is unavailable. Is the backend running?', {
                    cause: error,
                })
            }
            throw new Error('Server error. Try again in a minute', {
                cause: error,
            })
        }
        throw error
    }
}

function apiErrorMessage(res: Response, body: unknown): string {
    if (body != null && typeof body === 'object' && 'detail' in body) {
        const detail = (body as {detail?: unknown}).detail
        if (typeof detail === 'string' && detail.trim()) {
            return detail
        }
    }

    if (res.status >= 502 && res.status <= 504) {
        return 'API server is unavailable. Is the backend running?'
    }

    return `API error: ${res.status}`
}

/** Fetch helper for driver endpoints — does not throw UnauthorizedError (no login redirect). */
export async function driverFetch<T>(
    path: string,
    accessToken: string,
    init?: RequestInit,
): Promise<T> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        ...(init?.headers as Record<string, string>),
    }

    const res = await fetch(apiUrl(path), {
        ...init,
        headers,
    })

    if (res.status === 401) {
        throw new DriverAccessError()
    }

    if (!res.ok) {
        const body = await parseJsonResponse(res)
        const fieldErrors = parseDriverValidationFieldErrors(body)
        if (Object.keys(fieldErrors).length > 0) {
            throw new DriverValidationError(fieldErrors)
        }
        throw new Error(apiErrorMessage(res, body))
    }

    if (res.status === 204) {
        return undefined as T
    }

    const body = await parseJsonResponse(res)
    return body as T
}
